<template>
    <div :class="$style.button_content">
        <img
            :src="InstallArrow"
            :class="[$style.install_arrow, isAnimationVisible && $style.install_arrow_animated]"
        />
        <img
            :src="InstallBottom"
            :class="[$style.install_bottom, isAnimationVisible && $style.install_bottom_animated]"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import InstallArrow from '@/pages/Games/components/GameContainer/DesktopFooter/icons/install-arrow.svg'
import InstallBottom from '@/pages/Games/components/GameContainer/DesktopFooter/icons/install-bottom.svg'
import { useAppStore } from '@/store/app-store'
import { usePWAInstallStore } from '@/store/pwa-install/store'

const props = defineProps<{
    isHovered?: boolean
}>()

const isAnimationVisible = defineModel<boolean>('isAnimationVisible', { default: false })

const pwaStore = usePWAInstallStore()
const appStore = useAppStore()

const animationTimeout = ref<number>()
const ANIMATION_DELAY_MS = 3000
const isAnimatedButton = ref(false)

const isDesktop = computed(() => appStore.platform === 'desktop')

const computedIsAnimationVisible = computed(
    () =>
        isAnimatedButton.value && !props.isHovered && !pwaStore.installed && !pwaStore.pwaHasShowed && isDesktop.value,
)

watch(
    computedIsAnimationVisible,
    (value) => {
        isAnimationVisible.value = value
    },
    { immediate: true },
)

watch(
    () => pwaStore.hasDeferredPrompt,
    () => {
        clearTimeout(animationTimeout.value)
        animationTimeout.value = undefined
        isAnimatedButton.value = true
    },
)

onMounted(() => {
    if (!pwaStore.hasDeferredPrompt) {
        animationTimeout.value = window.setTimeout(() => {
            isAnimatedButton.value = true
            animationTimeout.value = undefined
        }, ANIMATION_DELAY_MS)
    } else {
        isAnimatedButton.value = true
    }
})
</script>

<style module>
.button_content {
    justify-content: center;
    align-items: center;
    flex-direction: column;
    height: 100%;
    gap: 3px;
    display: flex;
    position: relative;
}

.install_arrow {
    width: 16px;
    height: 16px;
}

.install_bottom {
    width: 16px;
}

.install_bottom_animated,
.install_arrow_animated {
    animation-duration: 2s;
    animation-timing-function: cubic-bezier(0.5, 0.9, 0.402, 1);
    animation-iteration-count: infinite;
}

.install_arrow_animated {
    animation-name: arrowBounce;
}

.install_bottom_animated {
    animation-name: bottomBounce;
}

@keyframes arrowBounce {
    0% {
        transform: translateY(-2px) scale(0.9);
    }
    25% {
        transform: translateY(-3px) scale(0.8);
    }
    50% {
        transform: translateY(2px) scale(1);
    }
    60% {
        transform: translateY(0) scale(1);
    }
    100% {
        transform: translateY(0) scale(1);
    }
}

@keyframes bottomBounce {
    0% {
        transform: scale(0.9);
    }
    25% {
        transform: scale(0.85);
    }
    50% {
        transform: scale(1);
    }
    60% {
        transform: scale(1);
    }
    100% {
        transform: scale(1);
    }
}
</style>
